// Toast store — the imperative API + state behind the design-system Toast
// (src/components/common/Toast.js).
//
// Why this exists: the app has 70+ native `alert()` calls and several places
// that hand-roll their own toast state (local useState + setTimeout) around the
// presentational <Toast/>. This centralises that into a single queue with
// auto-dismiss + dedup, exposed as an imperative API that works the same way
// `alert()` did — callable from any component (via useToast) or non-React code.
//
// Mirrors the react-hot-toast / sonner pattern: a module-level store, a single
// <Toaster/> mounted at the app root that subscribes and renders the stack.
//
// NOTE: files here are named to avoid a case-insensitive-FS clash with the
// existing `Toast.js` (so `toastStore` / `Toaster` / `useToast`, not `toast`).

let toasts = [];
const listeners = new Set();
let idCounter = 0;

// Errors stay up a touch longer (more text to read); everything else uses the
// default. Pass { duration } to override; Infinity / 0 = sticky until dismissed.
const DEFAULT_DURATION = 4000;
const ERROR_DURATION = 6000;

function emit() {
  for (const listener of listeners) listener(toasts);
}

// useSyncExternalStore contract: stable subscribe + snapshot. `toasts` only
// changes reference when we mutate, so getSnapshot is referentially stable
// between emits (no render loop).
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getToasts() {
  return toasts;
}

export function dismissToast(id) {
  const next = toasts.filter((t) => t.id !== id);
  if (next.length !== toasts.length) {
    toasts = next;
    emit();
  }
}

function addToast(message, type, options = {}) {
  // No message → no-op (guards the many `alert(maybeUndefined)` call sites).
  if (message === undefined || message === null || message === "") return null;

  const text = typeof message === "string" ? message : String(message);

  // Dedup: a rapid double-submit shouldn't stack two identical toasts. If one is
  // already showing, keep it (don't reset its timer) and return its id.
  const existing = toasts.find((t) => t.message === text && t.type === type);
  if (existing) return existing.id;

  const id = ++idCounter;
  const duration =
    options.duration !== undefined
      ? options.duration
      : type === "error"
        ? ERROR_DURATION
        : DEFAULT_DURATION;

  toasts = [...toasts, { id, message: text, type }];
  emit();

  if (duration !== Infinity && duration > 0) {
    setTimeout(() => dismissToast(id), duration);
  }

  return id;
}

// Imperative API. Stable module singleton — safe to import anywhere and to
// return directly from useToast() without memoisation.
export const toast = {
  success: (message, options) => addToast(message, "success", options),
  error: (message, options) => addToast(message, "error", options),
  info: (message, options) => addToast(message, "info", options),
  celebration: (message, options) => addToast(message, "celebration", options),
  copy: (message, options) => addToast(message, "copy", options),
  // Escape hatch when the type is dynamic; defaults to info.
  show: (message, options = {}) => addToast(message, options.type || "info", options),
  dismiss: dismissToast,
};
